// src/services/eventService.js
const API_BASE_URL = process.env.NODE_ENV === 'development' ? '/api' : 'https://nlngraclub.com/nlnapi';
const getToken = () => localStorage.getItem('nlngra_token');

const handleResponse = async(response) => {
    console.log('Response status:', response.status);
    console.log('Response headers:', [...response.headers.entries()]);

    const text = await response.text();
    console.log('Raw response:', text.substring(0, 500));

    if (!response.ok) {
        try {
            const errorData = JSON.parse(text);
            throw new Error(errorData.message || `HTTP ${response.status}`);
        } catch (e) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
    }

    // Look for JSON in the response
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
        try {
            const parsed = JSON.parse(jsonMatch[0]);
            console.log('Parsed JSON:', parsed);
            return parsed;
        } catch (e) {
            console.error('JSON parse error:', e);
            throw new Error('Invalid JSON format');
        }
    }

    throw new Error('No valid JSON found in response');
};

export const eventService = {
    // Get all events (public)
    getEvents: async(upcoming = true, limit = 20) => {
        try {
            const response = await fetch(`${API_BASE_URL}/events.php?action=list&upcoming=${upcoming}&limit=${limit}&_t=${Date.now()}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get events error:', error);
            throw error;
        }
    },

    // Get event stats (admin only)
    getEventStats: async() => {
        try {
            const token = getToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const response = await fetch(`${API_BASE_URL}/events.php?action=admin_stats&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get event stats error:', error);
            throw error;
        }
    },

    // Create event (admin only) - FIXED WITH DEBUG
    createEvent: async(eventData, imageFile = null) => {
        try {
            const token = getToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            console.log('=== CREATE EVENT DEBUG ===');
            console.log('Event Data:', eventData);
            console.log('Image File:', imageFile);

            // ✅ STRICT CHECK
            const hasValidFile = imageFile instanceof File;

            if (hasValidFile) {
                const formData = new FormData();
                formData.append('action', 'admin_create');

                for (const [key, value] of Object.entries(eventData)) {
                    if (value !== null && value !== undefined && value !== '') {
                        formData.append(key, value);
                    }
                }
                console.log('TYPE CHECK:', {
                    isFile: imageFile instanceof File,
                    type: typeof imageFile,
                    value: imageFile
                });
                // ✅ ONLY APPEND REAL FILE
                formData.append('event_image', imageFile);

                console.log('✅ Sending FORM DATA with real file');

                const response = await fetch(`${API_BASE_URL}/events.php`, {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token
                    },
                    body: formData
                });

                return await handleResponse(response);

            } else {
                console.log('⚠️ No valid file, sending JSON');

                // ✅ REMOVE ANY FAKE IMAGE FIELD
                const cleanData = {...eventData };
                delete cleanData.event_image;

                const response = await fetch(`${API_BASE_URL}/events.php?action=admin_create`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + token
                    },
                    body: JSON.stringify(cleanData)
                });

                return await handleResponse(response);
            }

        } catch (error) {
            console.error('Create event error:', error);
            throw error;
        }
    },

    // Update event (admin only) - FIXED
    updateEvent: async(eventId, eventData, imageFile = null) => {
        try {
            const token = getToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            console.log('=== UPDATE EVENT DEBUG ===');
            console.log('Event ID:', eventId);
            console.log('Event Data:', eventData);
            console.log('Image File:', imageFile ? {
                name: imageFile.name,
                type: imageFile.type,
                size: imageFile.size
            } : 'No new image');

            if (imageFile) {
                // Use FormData for file upload
                const formData = new FormData();
                // CRITICAL: Add action as a form field
                formData.append('action', 'admin_update');
                formData.append('event_id', eventId);
                for (const [key, value] of Object.entries(eventData)) {
                    if (value !== null && value !== undefined && value !== '') {
                        formData.append(key, value);
                    }
                }
                formData.append('event_image', imageFile);

                console.log('FormData fields:', Array.from(formData.keys()));

                const response = await fetch(`${API_BASE_URL}/events.php`, {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token
                    },
                    body: formData
                });
                return await handleResponse(response);
            } else {
                // JSON without image
                const response = await fetch(`${API_BASE_URL}/events.php?action=admin_update`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + token
                    },
                    body: JSON.stringify({
                        event_id: eventId,
                        ...eventData
                    })
                });
                return await handleResponse(response);
            }
        } catch (error) {
            console.error('Update event error:', error);
            throw error;
        }
    },

    // Delete event (admin only)
    deleteEvent: async(eventId) => {
        try {
            const token = getToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const response = await fetch(`${API_BASE_URL}/events.php?action=admin_delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token
                },
                body: JSON.stringify({ event_id: eventId })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Delete event error:', error);
            throw error;
        }
    },

    // Get event registrations (admin only)
    getEventRegistrations: async(eventId) => {
        try {
            const token = getToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const response = await fetch(`${API_BASE_URL}/events.php?action=admin_get_registrations&event_id=${eventId}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get event registrations error:', error);
            throw error;
        }
    }
};